import React, { useState, useEffect } from 'react';
import {
  PuzzlePieceIcon,
  PlusIcon,
  MinusIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { API_URL } from '../../../config/api';

const AddonSelector = ({ serviceId, service, serviceToken, selectedAddons, onAddonsChange, participantCount = 1 }) => {
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(false);

  // Check availability for all PER_ITEM addons on component mount
  useEffect(() => {
    const checkInitialAvailability = async () => {
      if (!service?.addons || !serviceToken) return;

      for (const addon of service.addons) {
        if (addon.rateType === 'PER_ITEM') {
          await checkAvailability(addon, 1);
        }
      }
    };

    checkInitialAvailability();
  }, [service, serviceToken]);

  const checkAvailability = async (addon, requestedQuantity) => {
    if (addon.rateType !== 'PER_ITEM') return { available: true, canReserve: true };

    // Don't check availability if no service token is provided
    if (!serviceToken) {
      console.warn('No service token provided for availability check');
      return { available: true, canReserve: true };
    }

    try {
      const addonId = addon.id || addon.name;
      console.log('🔍 DEBUG: Checking availability for addon', {
        addonName: addon.name,
        addonId,
        requestedQuantity
      });

      const response = await fetch(
        `${API_URL}/public/addon/availability?serviceToken=${serviceToken}&addonId=${addonId}&quantity=${requestedQuantity}`
      );

      if (response.ok) {
        const result = await response.json();
        console.log('🔍 DEBUG: Availability check result', {
          addonName: addon.name,
          result: result.data
        });

        if (result.success) {
          // Store availability info for UI display
          setAvailability(prev => ({
            ...prev,
            [addonId]: result.data
          }));
          return result.data;
        } else {
          console.error('Availability check failed:', result.error);
        }
      } else {
        console.error('Availability check request failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error checking availability:', error);
    }

    return { available: true, canReserve: true }; // Default to available if check fails
  };

  const handleQuantityChange = async (addon, newQuantity) => {
    // Ensure addon has a unique identifier - use name as fallback if no ID
    const addonId = addon.id || addon.name;
    const addonWithId = { ...addon, id: addonId };

    // Debug addon selection
    console.log('🔧 DEBUG: handleQuantityChange called', {
      addonName: addon.name,
      originalAddonId: addon.id,
      effectiveAddonId: addonId,
      newQuantity,
      currentSelectedAddons: selectedAddons.map(a => ({ id: a.id, name: a.name }))
    });

    // Check availability for PER_ITEM addons first (this includes total available quantity check)
    if (addonWithId.rateType === 'PER_ITEM' && newQuantity > 0) {
      setLoading(true);
      const availabilityCheck = await checkAvailability(addonWithId, newQuantity);
      setLoading(false);

      if (!availabilityCheck.canReserve) {
        alert(`Only ${availabilityCheck.availableQuantity} items available for ${addonWithId.name}`);
        return;
      }
    }

    // Check max per booking limit (only if it's set and more restrictive than available quantity)
    const maxAllowed = addonWithId.maxPerBooking || null;
    if (maxAllowed && newQuantity > maxAllowed) {
      alert(`Maximum ${maxAllowed} items allowed per booking for ${addonWithId.name}`);
      return;
    }

    const updatedAddons = [...selectedAddons];
    const existingIndex = updatedAddons.findIndex(selected => selected.id === addonId);

    console.log('🔧 DEBUG: Finding addon in selectedAddons', {
      effectiveAddonId: addonId,
      existingIndex,
      selectedAddonsIds: selectedAddons.map(s => s.id),
      foundMatch: existingIndex >= 0
    });

    if (newQuantity === 0) {
      // Remove addon if quantity is 0
      if (existingIndex >= 0) {
        updatedAddons.splice(existingIndex, 1);
      }
    } else {
      // Update or add addon with quantity
      const addonData = {
        id: addonId,
        name: addonWithId.name,
        rate: addonWithId.rate,
        rateType: addonWithId.rateType,
        quantity: newQuantity
      };

      if (existingIndex >= 0) {
        updatedAddons[existingIndex] = addonData;
      } else {
        updatedAddons.push(addonData);
      }
    }

    // Deduplicate addons by ID (safety measure)
    const deduplicatedAddons = [];
    const seenIds = new Set();

    for (const addon of updatedAddons) {
      if (!seenIds.has(addon.id)) {
        deduplicatedAddons.push(addon);
        seenIds.add(addon.id);
      } else {
        console.warn('🚨 DEBUG: Duplicate addon detected and removed', {
          duplicateAddon: { id: addon.id, name: addon.name, quantity: addon.quantity }
        });
      }
    }

    // Debug final addon list before sending to parent
    console.log('🚀 DEBUG: Final addons being sent to parent', {
      originalCount: updatedAddons.length,
      deduplicatedCount: deduplicatedAddons.length,
      deduplicatedAddons: deduplicatedAddons.map(a => ({ id: a.id, name: a.name, quantity: a.quantity }))
    });

    onAddonsChange(deduplicatedAddons);
  };

  const getSelectedQuantity = (addon) => {
    const addonId = addon.id || addon.name;
    const selected = selectedAddons.find(selected => selected.id === addonId);
    const quantity = selected ? (selected.quantity || 1) : 0;

    // Debug quantity lookup
    console.log('📊 DEBUG: getSelectedQuantity', {
      addonName: addon.name,
      originalAddonId: addon.id,
      effectiveAddonId: addonId,
      selectedAddon: selected ? { id: selected.id, name: selected.name } : null,
      quantity,
      allSelectedIds: selectedAddons.map(s => s.id)
    });

    return quantity;
  };

  const calculateAddonPrice = (addon, quantity) => {
    const baseRate = parseFloat(addon.rate) || 0;

    // Debug addon pricing calculation
    console.log('💰 DEBUG: calculateAddonPrice', {
      addonName: addon.name,
      rateType: addon.rateType,
      baseRate,
      quantity,
      participantCount
    });

    switch (addon.rateType) {
      case 'PER_PERSON':
        return baseRate * participantCount * quantity;
      case 'PER_ITEM':
        return baseRate * quantity;
      case 'FIXED':
      case 'FLAT_RATE': // Handle both FIXED and FLAT_RATE
      default:
        return baseRate; // For flat rate, don't multiply by quantity
    }
  };

  const formatRateType = (rateType) => {
    switch (rateType) {
      case 'PER_PERSON':
        return 'per person';
      case 'PER_ITEM':
        return 'per item';
      case 'FIXED':
      case 'FLAT_RATE':
        return 'flat rate';
      default:
        return rateType.toLowerCase().replace('_', ' ');
    }
  };

  if (!service?.addons || service.addons.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center mb-4">
        <PuzzlePieceIcon className="h-5 w-5 text-purple-500 mr-2" />
        <h3 className="text-lg font-medium text-gray-900">Available Add-ons</h3>
      </div>

      <div className="space-y-4">
        {service.addons.map((addon, index) => {
          // Debug addon rendering
          console.log('🎨 DEBUG: Rendering addon', {
            name: addon.name,
            id: addon.id,
            index,
            hasId: !!addon.id,
            keyUsed: addon.id || index
          });

          const selectedQuantity = getSelectedQuantity(addon);
          const isPerItem = addon.rateType === 'PER_ITEM';
          const baseRate = parseFloat(addon.rate) || 0;

          // Debug pricing precision issue
          console.log(`💰 DEBUG: Addon "${addon.name}" - Raw rate: "${addon.rate}", Parsed rate: ${baseRate}, Type: ${typeof addon.rate}`);
          if (addon.name === 'Alcohol') {
            console.log(`💰 DEBUG: Full addon object:`, addon);
          }

          return (
            <div key={addon.id || `addon-${addon.name}-${index}`} className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h4 className="text-sm font-medium text-gray-900">{addon.name}</h4>
                    {isPerItem && (
                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <InformationCircleIcon className="h-3 w-3 mr-1" />
                        Limited quantity
                      </span>
                    )}
                  </div>

                  <div className="text-sm text-gray-600 mb-3">
                    <span className="font-medium">${baseRate.toFixed(2)}</span>
                    <span className="text-gray-500 ml-1">
                      {formatRateType(addon.rateType)}
                    </span>
                    {addon.maxPerBooking && (
                      <span className="ml-2 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                        Max {addon.maxPerBooking} per booking
                      </span>
                    )}
                  </div>

                  {/* Quantity Selection for PER_ITEM */}
                  {isPerItem ? (
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-700">Quantity:</span>
                      <div className="flex items-center border border-gray-300 rounded-md">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(addon, Math.max(0, selectedQuantity - 1))}
                          disabled={selectedQuantity <= 0 || loading}
                          className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        >
                          <MinusIcon className="h-4 w-4" />
                        </button>
                        <span className="px-3 py-2 text-sm border-l border-r border-gray-300 min-w-[3rem] text-center">
                          {selectedQuantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(addon, selectedQuantity + 1)}
                          disabled={
                            loading ||
                            (addon.maxPerBooking && selectedQuantity >= addon.maxPerBooking) ||
                            (availability[addon.id || addon.name] && !availability[addon.id || addon.name].canReserve && selectedQuantity >= availability[addon.id || addon.name].availableQuantity) ||
                            (availability[addon.id || addon.name] && selectedQuantity + 1 > availability[addon.id || addon.name].availableQuantity)
                          }
                          className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                          title={
                            addon.maxPerBooking && selectedQuantity >= addon.maxPerBooking ?
                              `Maximum ${addon.maxPerBooking} allowed per booking` :
                              availability[addon.id || addon.name] && selectedQuantity + 1 > availability[addon.id || addon.name].availableQuantity ?
                                `Only ${availability[addon.id || addon.name].availableQuantity} available in inventory` : ''
                          }
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </div>

                      {selectedQuantity > 0 && (
                        <div className="text-sm text-green-600 flex items-center">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          ${calculateAddonPrice(addon, selectedQuantity).toFixed(2)} total
                        </div>
                      )}

                      {addon.maxPerBooking && selectedQuantity >= addon.maxPerBooking && (
                        <div className="text-xs text-orange-600 flex items-center mt-1">
                          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                          Maximum limit reached ({addon.maxPerBooking} per booking)
                        </div>
                      )}

                      {addon.totalQuantity && selectedQuantity >= addon.totalQuantity && (
                        <div className="text-xs text-red-600 flex items-center mt-1">
                          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                          Total inventory limit reached ({addon.totalQuantity} available)
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Checkbox for non-quantity addons */
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedQuantity > 0}
                        onChange={(e) => {
                          handleQuantityChange(addon, e.target.checked ? 1 : 0);
                        }}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Add to booking
                        {selectedQuantity > 0 && (
                          <span className="ml-2 text-green-600 font-medium">
                            (+${calculateAddonPrice(addon, selectedQuantity).toFixed(2)})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Show availability info for PER_ITEM addons */}
              {isPerItem && availability[addon.id] && (
                <div className="mt-3 p-2 bg-gray-50 rounded text-xs">
                  {availability[addon.id].canReserve ? (
                    <div className="text-green-600 flex items-center">
                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                      {availability[addon.id].availableQuantity} available
                    </div>
                  ) : (
                    <div className="text-red-600 flex items-center">
                      <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                      Only {availability[addon.id].availableQuantity} available
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary of selected addons */}
      {selectedAddons.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Selected Add-ons:</h4>
          <div className="space-y-1">
            {selectedAddons.map((addon, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {addon.name} {addon.quantity && addon.quantity > 1 && `(x${addon.quantity})`}
                </span>
                <span className="font-medium">
                  +${calculateAddonPrice(addon, addon.quantity || 1).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-2">
          <div className="inline-flex items-center text-sm text-gray-600">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Checking availability...
          </div>
        </div>
      )}
    </div>
  );
};

export default AddonSelector;